import { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, X, Image as ImageIcon } from 'lucide-react';

interface MediaItem {
  id: string;
  mediaType: 'image' | 'video';
  url: string;
  caption?: string;
}

interface Props {
  media: MediaItem[];
}

function getYouTubeEmbedUrl(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const match = url.match(p);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
  }
  return null;
}

function getVideoThumbnail(url: string): string | null {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`;
  return null;
}

export function MediaCarousel({ media }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!media || media.length === 0) return null;

  const currentItem = media[currentIndex];

  const goNext = () => setCurrentIndex((i) => (i + 1) % media.length);
  const goPrev = () => setCurrentIndex((i) => (i - 1 + media.length) % media.length);

  return (
    <div className="mb-6">
      {/* Main display */}
      <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video">
        {currentItem.mediaType === 'image' ? (
          <img
            src={currentItem.url}
            alt={currentItem.caption || 'Foto de la campaña'}
            className="w-full h-full object-contain cursor-pointer"
            onClick={() => setLightboxOpen(true)}
          />
        ) : (
          <div className="w-full h-full">
            {getYouTubeEmbedUrl(currentItem.url) ? (
              <iframe
                src={getYouTubeEmbedUrl(currentItem.url)!}
                title={currentItem.caption || 'Video de la campaña'}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            ) : (
              <a
                href={currentItem.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full h-full text-white hover:text-indigo-300 transition-colors"
              >
                <div className="text-center">
                  <Play className="w-16 h-16 mx-auto mb-2" />
                  <p className="text-sm">Ver video en plataforma externa</p>
                </div>
              </a>
            )}
          </div>
        )}

        {/* Caption overlay */}
        {currentItem.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <p className="text-white text-sm">{currentItem.caption}</p>
          </div>
        )}

        {/* Navigation arrows */}
        {media.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Counter */}
        {media.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            {currentIndex + 1} / {media.length}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {media.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {media.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setCurrentIndex(index)}
              className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              {item.mediaType === 'image' ? (
                <img src={item.url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                  {getVideoThumbnail(item.url) ? (
                    <img src={getVideoThumbnail(item.url)!} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Play className="w-5 h-5 text-white" />
                  )}
                  <Play className="absolute w-4 h-4 text-white drop-shadow" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox for images */}
      {lightboxOpen && currentItem.mediaType === 'image' && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            aria-label="Cerrar"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={currentItem.url}
            alt={currentItem.caption || 'Foto'}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
